import type { FC, FormEvent, ReactNode } from 'react'
import type { NavCategory } from '@/types'
import type { ListingResult } from '@/lib/server/listing'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight, PackageSearch, Search, SlidersHorizontal, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useI18n } from '@/lib/i18n'
import { Button } from '@/components/ui/button'
import { Select } from '@/components/ui/field'
import { CmsText } from '@/components/store/cms-text'
import { Num } from '@/components/store/price'
import { ProductGrid } from '@/components/store/product-card'

interface ProductListingProps {
    result: ListingResult
    categories: Array<NavCategory>
    activeCategoryId: string | null
    showSearch?: boolean
}

export const ProductListing: FC<ProductListingProps> = ({ result, categories, activeCategoryId, showSearch = true }): ReactNode => {
    const { tx, txn, isRtl } = useI18n()
    const router = useRouter()
    const [pending, setPending] = useState(false)
    const [search, setSearch] = useState(result.filters.q)
    const { filters } = result

    useEffect(() => {
        setSearch(result.filters.q)
    }, [result.filters.q])

    useEffect(() => {
        const start = (): void => setPending(true)
        const done = (): void => setPending(false)

        router.events.on('routeChangeStart', start)
        router.events.on('routeChangeComplete', done)
        router.events.on('routeChangeError', done)

        return () => {
            router.events.off('routeChangeStart', start)
            router.events.off('routeChangeComplete', done)
            router.events.off('routeChangeError', done)
        }
    }, [router.events])

    const update = (changes: Record<string, string | null>): void => {
        const query: Record<string, string> = {}

        for (const [key, value] of Object.entries(router.query)) {
            if (typeof value === 'string') query[key] = value
        }

        for (const [key, value] of Object.entries(changes)) {
            if (value === null || value === '') delete query[key]
            else query[key] = value
        }

        if (!('page' in changes)) delete query.page

        void router.push({ pathname: router.pathname, query }, undefined, { scroll: 'page' in changes })
    }

    const submitSearch = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault()
        update({ q: search.trim() || null })
    }

    const hasFilters = Boolean(filters.q || filters.offers || filters.stock || filters.sort !== 'featured')
    const Prev = isRtl ? ChevronRight : ChevronLeft
    const Next = isRtl ? ChevronLeft : ChevronRight

    const chip = (active: boolean): string => cn(
        'inline-flex h-10 shrink-0 items-center gap-2 rounded-full px-4 text-sm font-medium transition-colors',
        active ? 'bg-navy-900 text-white shadow-sm' : 'bg-white text-navy-800 ring-1 ring-navy-100 hover:bg-navy-50 hover:ring-navy-200'
    )

    return (
        <div className='flex flex-col gap-8'>
            <div className='flex flex-col gap-4'>
                <nav className='-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 no-scrollbar sm:mx-0 sm:flex-wrap sm:px-0' aria-label={tx('الأقسام', 'Categories')}>
                    <Link href='/products' className={chip(activeCategoryId === null)} aria-current={activeCategoryId === null ? 'page' : undefined}>
                        {tx('الكل', 'All')}
                    </Link>

                    {categories.map(category => (
                        <Link key={category.id} href={`/categories/${category.slug}`} className={chip(activeCategoryId === category.id)} aria-current={activeCategoryId === category.id ? 'page' : undefined}>
                            <CmsText table='categories' id={category.id} field={category.name} />
                        </Link>
                    ))}
                </nav>

                <div className='flex flex-col gap-3 rounded-3xl bg-white p-3 ring-1 ring-navy-100 sm:flex-row sm:items-center sm:p-2'>
                    {showSearch && (
                        <form onSubmit={submitSearch} className='relative flex-1' role='search'>
                            <label htmlFor='listing-search' className='sr-only'>{tx('ابحث في المنتجات', 'Search products')}</label>
                            <Search className='pointer-events-none absolute start-4 top-1/2 size-4 -translate-y-1/2 text-navy-400' aria-hidden='true' />
                            <input
                                id='listing-search'
                                type='search'
                                value={search}
                                onChange={event => setSearch(event.target.value)}
                                placeholder={tx('ابحث بالاسم أو الكود...', 'Search by name or code...')}
                                className='h-11 w-full rounded-2xl bg-navy-50/70 ps-11 pe-4 text-sm text-navy-950 placeholder:text-navy-400 focus:bg-white focus:outline-none focus:ring-4 focus:ring-cyan-400/20'
                            />
                        </form>
                    )}

                    <div className='flex flex-wrap items-center gap-2'>
                        <button type='button' onClick={() => update({ offers: filters.offers ? null : '1' })} className={chip(filters.offers)} aria-pressed={filters.offers}>
                            {tx('عليها عروض', 'On offer')}
                        </button>

                        <button type='button' onClick={() => update({ stock: filters.stock ? null : '1' })} className={chip(filters.stock)} aria-pressed={filters.stock}>
                            {tx('المتوفر فقط', 'In stock only')}
                        </button>

                        <div className='flex items-center gap-2'>
                            <SlidersHorizontal className='size-4 text-navy-400' aria-hidden='true' />
                            <label htmlFor='listing-sort' className='sr-only'>{tx('الترتيب', 'Sort by')}</label>
                            <Select id='listing-sort' value={filters.sort} onChange={event => update({ sort: event.target.value === 'featured' ? null : event.target.value })} className='h-10 rounded-full text-sm'>
                                <option value='featured'>{tx('المميز أولاً', 'Featured first')}</option>
                                <option value='newest'>{tx('الأحدث', 'Newest')}</option>
                                <option value='price-asc'>{tx('السعر: من الأقل', 'Price: low to high')}</option>
                                <option value='price-desc'>{tx('السعر: من الأعلى', 'Price: high to low')}</option>
                                <option value='name'>{tx('الاسم', 'Name')}</option>
                            </Select>
                        </div>
                    </div>
                </div>

                <div className='flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground'>
                    <p aria-live='polite'>
                        {txn('{n} منتج', '{n} products', { n: <Num value={result.total} className='font-semibold text-navy-900' /> })}
                        {filters.q && (
                            <>
                                {' '}
                                {tx('لنتيجة البحث', 'for')}
                                {' '}
                                <span className='font-semibold text-navy-900'>{filters.q}</span>
                            </>
                        )}
                    </p>

                    {hasFilters && (
                        <button type='button' onClick={() => update({ q: null, offers: null, stock: null, sort: null })} className='inline-flex items-center gap-1.5 font-medium text-navy-700 hover:text-cyan-700'>
                            <X className='size-4' aria-hidden='true' />
                            {tx('مسح الفلاتر', 'Clear filters')}
                        </button>
                    )}
                </div>
            </div>

            <div className={cn('transition-opacity duration-200', pending && 'pointer-events-none opacity-50')}>
                {result.products.length > 0
                    ? <ProductGrid products={result.products} priorityCount={4} />
                    : (
                        <div className='flex flex-col items-center gap-4 rounded-3xl bg-white px-6 py-16 text-center ring-1 ring-navy-100'>
                            <span className='flex size-16 items-center justify-center rounded-3xl bg-navy-50 text-navy-700'>
                                <PackageSearch className='size-7' strokeWidth={1.5} aria-hidden='true' />
                            </span>
                            <div className='flex flex-col gap-1'>
                                <p className='text-lg font-semibold text-navy-950'>{tx('لا توجد منتجات مطابقة', 'No matching products')}</p>
                                <p className='text-sm text-muted-foreground'>{tx('جرّب كلمة بحث مختلفة أو امسح الفلاتر.', 'Try a different search or clear the filters.')}</p>
                            </div>
                            {hasFilters && <Button variant='outline' onClick={() => update({ q: null, offers: null, stock: null, sort: null })}>{tx('مسح الفلاتر', 'Clear filters')}</Button>}
                        </div>
                    )}
            </div>

            {result.pageCount > 1 && (
                <nav className='flex items-center justify-center gap-2' aria-label={tx('الصفحات', 'Pages')}>
                    <button type='button' disabled={result.page <= 1} onClick={() => update({ page: String(result.page - 1) })} className='inline-flex size-10 items-center justify-center rounded-full bg-white text-navy-800 ring-1 ring-navy-100 transition-colors hover:bg-navy-50 disabled:opacity-40' aria-label={tx('السابق', 'Previous')}>
                        <Prev className='size-4' />
                    </button>

                    {Array.from({ length: result.pageCount }, (_, index) => index + 1).map(page => (
                        <button
                            key={page}
                            type='button'
                            onClick={() => update({ page: String(page) })}
                            className={cn('inline-flex size-10 items-center justify-center rounded-full text-sm font-semibold tabular-nums transition-colors', page === result.page ? 'bg-navy-900 text-white' : 'bg-white text-navy-800 ring-1 ring-navy-100 hover:bg-navy-50')}
                            aria-current={page === result.page ? 'page' : undefined}
                        >
                            {page}
                        </button>
                    ))}

                    <button type='button' disabled={result.page >= result.pageCount} onClick={() => update({ page: String(result.page + 1) })} className='inline-flex size-10 items-center justify-center rounded-full bg-white text-navy-800 ring-1 ring-navy-100 transition-colors hover:bg-navy-50 disabled:opacity-40' aria-label={tx('التالي', 'Next')}>
                        <Next className='size-4' />
                    </button>
                </nav>
            )}
        </div>
    )
}
